"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./FirstRunChicky.module.css";

/** Chicky steps up onto the rim once the arriving sun is mostly up. */
const ARRIVES_AFTER_MS = 1_800;

/**
 * Chicky on the planet beside the rising sun, lit from behind: running in
 * place while the learner chooses, waving once a language is chosen, jumping
 * when the session starts. Frames come from the Chicky pet sprite sheet, cut
 * to the three rows used (`chicky-intro-sprite.webp`: 8 x 3 cells of 192 x
 * 208). Decorative: the intro's heading does the talking, so the sprite loads
 * at low priority. Chicky arrives once it has loaded and the sun is up.
 */
export function FirstRunChicky() {
  const spriteRef = useRef<HTMLImageElement>(null);
  const mountedAtRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const arrive = () => {
    if (timerRef.current !== null) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const wait = reduce ? 0 : ARRIVES_AFTER_MS - (performance.now() - mountedAtRef.current);
    timerRef.current = window.setTimeout(() => setLoaded(true), Math.max(0, wait));
  };

  useEffect(() => {
    mountedAtRef.current = performance.now();
    // A cached sprite may finish before the load handler is attached.
    if (spriteRef.current?.complete && spriteRef.current.naturalWidth > 0) arrive();
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className={styles.orbit} aria-hidden="true" data-loaded={loaded || undefined}>
      <div className={styles.figure}>
        <div className={styles.frame}>
          <img
            ref={spriteRef}
            className={styles.sprite}
            src="/ui/brand-kit/mascot/chicky-intro-sprite.webp"
            alt=""
            width={1536}
            height={624}
            decoding="async"
            fetchPriority="low"
            onLoad={arrive}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
}
